import {
  StrictMode,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, useQuery } from "@tanstack/react-query";
import { ApiError, locale as localeDefinition, negotiateLocale, type Locale } from "@tovi/shared";
import { Avatar, I18nProvider, useBrandTokens, useTranslate } from "@tovi/ui";
import "./styles.css";
import { ServiceScreen } from "@/screens/service.tsx";
import { StationScreen } from "@/screens/station.tsx";
import { SignInScreen } from "@/screens/sign-in.tsx";
import { FailureList } from "@/components/failures.tsx";
import { ShiftPanel } from "@/components/shift.tsx";
import { ModeSwitch } from "@/components/mode.tsx";
import { IdentifyScreen } from "@/screens/identify.tsx";
import { currentCashier, subscribeToCashier } from "@/lib/staff.ts";
import { useEmployeePhoto } from "@/lib/use-employee-photo.ts";
import { TillContext } from "@/lib/use-till.ts";
import { BillBookContext } from "@/lib/use-bills.ts";
import { BillBookStore } from "@/lib/bill-book.ts";
import { preferredLocale, setPreferredLocale } from "@/lib/locale.ts";
import { Till } from "@/lib/till.ts";
import { api, onSignedOut, token } from "@/lib/api.ts";

/**
 * The till (`08-pos-terminal-and-qr.md` § A).
 *
 * One long-lived screen, which is why there is no router: the cart is never
 * navigated away from, and everything else opens over it. A Capacitor shell
 * loads this and nothing else.
 */

/**
 * The till's language, in falling order of authority.
 *
 * A terminal is a fixed object in a fixed room, so it opens in the language the
 * **store** works in rather than the one this particular device shipped with —
 * `/me` is the authority and its first language wins. Below that sits whatever
 * was chosen on the sign-in screen and kept in device storage, and below that
 * the device's own list.
 *
 * The order is the whole point: the switcher on the sign-in screen is a setup
 * convenience for the moments before a store exists to ask about, and it must
 * not still be overriding head office a month later.
 */
function tillLocale(languages: Locale[] | undefined, preferred: Locale | null): Locale {
  return languages?.[0] ?? preferred ?? negotiateLocale(navigator.languages.join(","));
}

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // A till on a restaurant's wifi retries into a hole otherwise, and the
      // offline queue is what actually carries writes through. A 401 is not
      // retried at all: the second attempt fails the same way and only delays
      // the sign-in screen.
      retry: (attempts, error) =>
        !(error instanceof ApiError && error.code === "UNAUTHENTICATED") && attempts < 1,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Where the terminal's credential stands.
 *
 * `none` is a till nobody has set up yet; `expired` is one that was working a
 * moment ago and whose token stopped being accepted. They are not the same
 * screen, which is why they are not the same state: the second one has a
 * cashier standing at it, a bill on it, and possibly a settlement still in the
 * queue, and none of that may be thrown away to show a login.
 */
type Session = "none" | "live" | "expired";

/**
 * The sign-in screen, put in front of everything already on the till.
 *
 * A positioned overlay is not enough, and finding that out took opening the
 * app: the settle dialog is a native `<dialog>` shown with `showModal()`,
 * which sits in the browser's top layer above every z-index there is and makes
 * the rest of the document inert. Settling is also exactly when the queue
 * drains — so the likeliest 401 of all arrives with that dialog open, and the
 * sign-in form would have rendered underneath it and refused every keystroke.
 *
 * A `<dialog>` of its own is the fix: the top layer stacks in the order things
 * were opened, so this covers the settle dialog, takes the focus, and gives it
 * back untouched when it closes.
 */
function SessionLock({ children }: { children: ReactNode }) {
  const held = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = held.current;
    if (dialog && !dialog.open) dialog.showModal();
  }, []);

  return (
    <dialog
      ref={held}
      // Escape must not dismiss it. There is nothing behind it that can reach
      // the server, and a till that looks usable and silently sends nothing is
      // worse than one that plainly asks to be signed in.
      onCancel={(event) => event.preventDefault()}
      className="m-0 h-full max-h-none w-full max-w-none bg-surface-sunken p-0 text-ink-body"
    >
      {children}
    </dialog>
  );
}

function App() {
  const till = useMemo(() => new Till(), []);
  const bills = useMemo(() => new BillBookStore(till), [till]);
  const [session, setSession] = useState<Session>(() => (token() === null ? "none" : "live"));
  const [preferred, setPreferred] = useState(preferredLocale);

  useEffect(() => {
    // `start` is asynchronous and its teardown arrives with it, so the stop
    // has to be chained rather than called: unmounting before the queue has
    // resumed must still leave nothing ticking.
    const starting = till.start();
    return () => void starting.then((stop) => stop());
  }, [till]);

  useEffect(() => {
    // After the queue, so the recovery pass can see what is still waiting to
    // be sent and re-queue only the creates that are genuinely lost.
    const starting = bills.start();
    return () => void starting.then((stop) => stop());
  }, [bills]);

  /**
   * A 401 from anywhere — a screen's query or the queue's own drain — arrives
   * here and puts the sign-in screen in front of the person at the terminal.
   * Nothing else in the app has to know about authentication.
   */
  useEffect(() => onSignedOut(() => setSession("expired")), []);

  /**
   * Which person is at the keys, as opposed to which terminal this is.
   *
   * Outside React, like the queue and for the same reason: handing over must
   * not depend on which screen happens to be mounted, and the assertion is
   * read by `staffHeaders()` from anywhere.
   */
  const cashier = useSyncExternalStore(subscribeToCashier, currentCashier, currentCashier);
  const [showShift, setShowShift] = useState(false);
  /**
   * Which job this terminal is doing.
   *
   * A device rather than a person: the screen bolted above a pass is a kitchen
   * display all service, and the one on the counter is a till all service.
   * Held in component state rather than persisted, because the alternative — a
   * terminal that remembers it is a pass — is a cashier finding the wrong
   * screen after a restart with no obvious way back.
   */
  const [view, setView] = useState<"service" | "station">("service");

  const me = useQuery({
    queryKey: ["me"],
    queryFn: () =>
      api<{
        store: { name: string; languages: Locale[] };
        brand: { colorTokens: Record<string, string> };
      }>("/me"),
    enabled: session === "live",
  });

  const locale = tillLocale(me.data?.store.languages, preferred);
  const cashierPhoto = useEmployeePhoto(cashier?.id ?? null, cashier?.photoVersion ?? null);

  useBrandTokens(me.data?.brand);

  /**
   * The till owns its document, and a static `index.html` cannot know which
   * store the terminal belongs to — so the root element is told once `/me`
   * answers. This is not decoration: `--brand-line-height-body` keys off
   * `:root:lang(km)`, and Khmer stacks subscript consonants that clip without
   * the room that variable buys.
   */
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = localeDefinition(locale).direction;
  }, [locale]);

  const signIn = (
    <SignInScreen
      locale={locale}
      expired={session === "expired"}
      onLocaleChange={(next) => {
        setPreferredLocale(next);
        setPreferred(next);
      }}
      onSignedIn={() => {
        setSession("live");
        // Whatever is cached was read with the credential that just stopped
        // working, and the account now at the terminal need not be the one
        // that fetched it. Everything is read again; the queue goes out on the
        // new token immediately.
        void client.invalidateQueries();
        void till.drain();
      }}
    />
  );

  if (session === "none") {
    return <I18nProvider locale={locale}>{signIn}</I18nProvider>;
  }

  /**
   * Identifying the cashier comes after the terminal's own sign-in, per
   * `sign-in.tsx` — by scanning the till's QR with the staff app
   * (`identify.tsx`).
   *
   * Ahead of the service screen rather than as a banner on it: a shift is
   * attributed to whoever is on, and a till that let somebody ring up bills
   * before saying who they were would produce exactly the anonymous drawer
   * this exists to end.
   */
  if (cashier === null) {
    return (
      <I18nProvider locale={locale}>
        <IdentifyScreen onIdentified={() => setShowShift(true)} />
      </I18nProvider>
    );
  }

  return (
    <TillContext.Provider value={till}>
      <BillBookContext.Provider value={bills}>
        <I18nProvider locale={locale}>
          <div className="h-full">
            <header className="flex h-12 items-center justify-between border-b border-line bg-surface px-4">
              <span className="text-sm font-semibold text-ink-body">
                {me.data?.store.name ?? "TOVI"}
              </span>
              <div className="flex items-center gap-2">
                {/* Beside the mode badge rather than inside it: the badge is
                    about the connection and this is about a bill somebody has
                    to chase, and merging them buries the second. */}
                {/* The drawer, and whoever has it. First in the row because
                    it is the one thing a cashier taps at the start and end of
                    a service rather than mid-bill. */}
                <button
                  type="button"
                  onClick={() => setShowShift(true)}
                  className="flex items-center gap-2 rounded-control border border-line px-3 py-1 text-sm text-ink-body"
                >
                  <Avatar name={cashier.name} src={cashierPhoto ?? undefined} size={20} />
                  {cashier.name}
                </button>
                <ViewSwitch view={view} onChange={setView} />
                <FailureList />
                <ModeSwitch />
              </div>
            </header>
            <div className="h-[calc(100%-3rem)]">
              {view === "service" ? <ServiceScreen /> : <StationScreen />}
            </div>
          </div>
          {/* Over the service screen rather than instead of it. The open bills
            now survive an unmount — and a restart — but the screen behind this
            still holds the cashier's place in it, and taking that away at the
            moment a token expires is a needless second interruption. */}
          {showShift ? <ShiftPanel onClose={() => setShowShift(false)} /> : null}
          {session === "expired" ? <SessionLock>{signIn}</SessionLock> : null}
        </I18nProvider>
      </BillBookContext.Provider>
    </TillContext.Provider>
  );
}

/**
 * Swaps the terminal between taking orders and making them.
 *
 * Its own component only because `App` sits outside `I18nProvider` — the label
 * has to be translated, and the provider is in the tree below it.
 */
function ViewSwitch({
  view,
  onChange,
}: {
  view: "service" | "station";
  onChange: (next: "service" | "station") => void;
}): React.ReactElement {
  const t = useTranslate();
  return (
    <button
      type="button"
      onClick={() => onChange(view === "service" ? "station" : "service")}
      className="rounded-control border border-line px-3 py-1 text-sm text-ink-body"
    >
      {view === "service" ? t("pos.station.title") : t("pos.station.backToTill")}
    </button>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
